import React, { useEffect } from "react"
import ObjectiveNavigationTemplate from "views/templates/ObjectiveNavigationTemplate"
import { Card, Page } from "@shopify/polaris"
import {
  PILLAR_ENVIRONMENTAL,
  PILLAR_PROFIT,
  PILLAR_SOCIAL,
} from "utils/constants/pillars"
import { useAppContext } from "components/AppContext"
import usePillars from "views/organisms/pillars/usePillars"
import CompareTemplate from "views/templates/CompareTemplate"
import useComparedNodeStorage from "hooks/useComparedNodeStorage"
import useCompare from "hooks/useCompare"
import Error from "components/widgets/ScorePage/Error"
import Loading from "components/widgets/Loading"
import AccessModal from "components/AccessModal"
import PropTypes from "prop-types"
import CompareAssetsOrganism from "views/organisms/canvases/charts/compare/CompareAssetsOrganism"
import { shouldUseMetabaseApproach } from "services/compare/compareTypeService"
import { useTranslation } from "react-i18next"

const ComparePageContentTemplate = ({
  compareRequester,
  CompareScoreComponent,
}) => {
  const {
    parentOrganization,
    currentNode,
    subscription,
    currentOrganization,
    currentUser,
  } = useAppContext()

  const { t } = useTranslation()

  const hasCompareAccess = subscription?.features?.compare
  const {
    nodes: storedNodes,
    setStoredNodes,
    isStoredNodesInitialized,
  } = useComparedNodeStorage(hasCompareAccess ? currentNode?.id : null)

  const pillars = usePillars({
    pageName: "/compare",
    [PILLAR_SOCIAL]: { disabled: true },
    [PILLAR_PROFIT]: { disabled: true },
  })

  const {
    isComparedScoresLoading,
    isComparedScoresError,
    comparedScores,
    comparedNodes,
    search,
    onDelete,
  } = useCompare(
    storedNodes,
    isStoredNodesInitialized && hasCompareAccess,
    compareRequester
  )

  useEffect(() => {
    if (comparedNodes && Array.isArray(comparedNodes)) {
      setStoredNodes(comparedNodes)
    }
  }, [comparedNodes, setStoredNodes])

  const display = () => {
    if (isComparedScoresError) return <Error />

    if (isComparedScoresLoading)
      return (
        <Card>
          <Card.Section>
            <Loading />
          </Card.Section>
        </Card>
      )

    if (!comparedScores) return null

    const comparedNodesAndCurrentNode = [...comparedNodes, currentNode]

    const shouldUseMetabase = shouldUseMetabaseApproach(
      comparedNodesAndCurrentNode
    )

    if (shouldUseMetabase) {
      return <CompareAssetsOrganism comparedNodes={comparedNodes} />
    }

    return (
      <CompareScoreComponent
        comparedScores={comparedScores}
        comparedNodes={comparedNodes}
      />
    )
  }

  const paywallTexts = {
    description: t("common:subscription_feature_missing.compare.description"),
    mailSubject: t(
      "common:subscription_feature_missing.compare.mailTemplateSubject",
      { currentOrganizationName: currentOrganization?.name || "" }
    ),
    mailBody: t(
      "common:subscription_feature_missing.compare.mailTemplateBody",
      {
        currentUserFirstname: currentUser?.firstname || "",
        currentUserLastname: currentUser?.lastname || "",
      }
    ),
  }

  if (!subscription) return <Loading />

  if (hasCompareAccess && !isStoredNodesInitialized) return <Loading />

  return (
    <ObjectiveNavigationTemplate>
      <Page fullWidth>
        <CompareTemplate
          parentOrganization={parentOrganization || []}
          themeType={PILLAR_ENVIRONMENTAL}
          pillars={pillars}
          search={search}
          onDelete={onDelete}
          comparedNodes={comparedNodes}
          storedNodes={storedNodes}
          setStoredNodes={setStoredNodes}
        >
          {hasCompareAccess ? display() : <AccessModal open scoped texts={paywallTexts} />}
        </CompareTemplate>
      </Page>
    </ObjectiveNavigationTemplate>
  )
}

ComparePageContentTemplate.propTypes = {
  compareRequester: PropTypes.func.isRequired,
  CompareScoreComponent: PropTypes.elementType.isRequired,
}

export default ComparePageContentTemplate
